// Source-type metadata that is not validation: E2 boxes (spec §1), Turkish field labels, header aliases, name-based suggestion (spec §2).
// Static, no I/O: the web reads it directly (ADR-0001).
import { fold } from "./normalize";
import { SOURCE_TYPES, type SourceType } from "./rows";

export const BOX_NAMES = ["customs", "erp", "accounting", "security"] as const;
export type BoxName = (typeof BOX_NAMES)[number];
/** Spec §1 "Kaynak kutuları". Order inside a box is the order of the files on E2. */
export const SOURCE_BOXES: Record<BoxName, SourceType[]> = {
  customs: ["GUMRUK_ITH", "GUMRUK_IHR"],
  erp: ["ERP_STOK", "RECETE", "DIIB", "GTIP_MALZEME"],
  accounting: ["MUHASEBE"],
  security: ["GUVENLIK", "KAMERA"],
};

/** Screen label of a source type (glossary §3, #164). On screen always "Etiket (KOD)": use `sourceTypeText`. */
export const SOURCE_TYPE_LABEL: Record<SourceType, string> = {
  GUMRUK_ITH: "İthalat beyannamesi", GUMRUK_IHR: "İhracat beyannamesi", ERP_STOK: "Stok hareketleri", RECETE: "Reçete", DIIB: "DİİB",
  GTIP_MALZEME: "GTİP–malzeme eşleşmesi", MUHASEBE: "Muhasebe hareketleri", GUVENLIK: "Güvenlik giriş-çıkış", KAMERA: "Kamera kayıt sürekliliği",
};
/** "Stok hareketleri (ERP_STOK)"; an unknown code stays as it is. */
export const sourceTypeText = (code: string) => (code in SOURCE_TYPE_LABEL ? `${SOURCE_TYPE_LABEL[code as SourceType]} (${code})` : code);

/** Turkish label first, then aliases (spec §2: `miktar`, `qty`, `adet`, `net miktar` …). Compared after `fold`. */
export const FIELDS: Record<string, { label: string; aliases: string[] }> = {
  declarationNo: { label: "Beyanname No", aliases: ["Beyanname Numarası", "Tescil No", "GTB No"] },
  registrationDate: { label: "Tescil Tarihi", aliases: ["Beyanname Tarihi", "Tarih"] },
  itemNo: { label: "Kalem No", aliases: ["Kalem", "Kalem Numarası", "Sıra No"] },
  hsCode: { label: "GTİP", aliases: ["GTİP Kodu", "GTİP No", "HS Kodu", "HS Code"] },
  quantity: { label: "Miktar", aliases: ["Qty", "Quantity", "Adet", "Net Miktar"] },
  unit: { label: "Birim", aliases: ["Ölçü Birimi", "Unit", "UOM"] },
  ipaNo: { label: "DİİB No", aliases: ["Belge No", "DİİB Belge No", "İzin Belge No"] },
  statisticalValue: { label: "İstatistiki Kıymet", aliases: ["İstatistik Kıymet", "Kıymet"] },
  currency: { label: "Döviz", aliases: ["Döviz Cinsi", "Para Birimi", "Currency"] },
  procedureCode: { label: "Rejim Kodu", aliases: ["Rejim"] },
  originCountry: { label: "Menşe", aliases: ["Menşe Ülke"] },
  destinationCountry: { label: "Alıcı Ülke", aliases: ["Varış Ülkesi"] },
  movementNo: { label: "Hareket No", aliases: ["Hareket Numarası"] },
  date: { label: "Tarih", aliases: ["Hareket Tarihi", "Date"] },
  movementType: { label: "Hareket Tipi", aliases: ["Hareket Türü", "Tip"] },
  materialCode: { label: "Malzeme Kodu", aliases: ["Stok Kodu", "Malzeme"] },
  referenceDoc: { label: "Referans Belge", aliases: ["Referans", "Referans No"] },
  workOrderNo: { label: "İş Emri No", aliases: ["İş Emri"] },
  warehouse: { label: "Depo", aliases: ["Depo Kodu"] },
  productCode: { label: "Mamul Kodu", aliases: ["Mamul", "Ürün Kodu"] },
  componentCode: { label: "Bileşen Kodu", aliases: ["Bileşen", "Hammadde Kodu"] },
  wastePct: { label: "Fire Oranı (%)", aliases: ["Fire Oranı", "Fire", "Fire %"] },
  startDate: { label: "Başlangıç", aliases: ["Başlangıç Tarihi"] },
  endDate: { label: "Bitiş", aliases: ["Bitiş Tarihi"] },
  commitmentType: { label: "Taahhüt Tipi", aliases: ["Taahhüt Türü"] },
  valueUsd: { label: "Değer (USD)", aliases: ["Değer USD", "USD Değer", "Değer"] },
  voucherNo: { label: "Fiş No", aliases: ["Fiş Numarası", "Yevmiye No"] },
  lineSeq: { label: "Satır Sırası", aliases: [] }, // sistem üretir (rows.ts); eşleme listesinde yok (fieldsOf), yalnız etiket için
  debit: { label: "Borç", aliases: ["Debit"] },
  credit: { label: "Alacak", aliases: ["Credit"] },
  documentNo: { label: "Belge No", aliases: ["Belge Numarası", "Evrak No"] },
  accountCode: { label: "Hesap Kodu", aliases: ["Hesap"] },
  entryNo: { label: "Kayıt No", aliases: ["Kayıt Numarası"] },
  time: { label: "Saat", aliases: ["Time"] },
  entryType: { label: "Kayıt Tipi", aliases: ["Giriş Tipi"] },
  gate: { label: "Kapı", aliases: ["Kapı Adı"] },
  direction: { label: "Yön", aliases: ["Giriş Çıkış"] },
  plate: { label: "Plaka", aliases: ["Araç Plaka"] },
  cameraCode: { label: "Kamera Kodu", aliases: ["Kamera No", "Kamera"] },
  cameraLocation: { label: "Kamera Konumu", aliases: ["Konum"] },
  outageMin: { label: "Kesinti (dk)", aliases: ["Kesinti", "Kesinti Süresi"] },
  totalMin: { label: "Toplam Süre", aliases: ["Toplam Süre (dk)"] },
  cameraStatus: { label: "Durum", aliases: [] },
  conversionFactor: { label: "Dönüşüm Katsayısı", aliases: ["Katsayı", "Çevrim Katsayısı"] },
};

/** ADR-0018 event-date field per dated source type. RECETE, GTIP_MALZEME and DIIB are always full state (DIIB startDate/endDate is the document term, not an event date). */
export const EVENT_DATE_FIELD: Partial<Record<SourceType, string>> = {
  GUMRUK_ITH: "registrationDate", GUMRUK_IHR: "registrationDate", ERP_STOK: "date", MUHASEBE: "date", GUVENLIK: "date", KAMERA: "date",
};

/** Fields the user can map for a type, required first (the system-made `lineSeq` is not one). */
export const fieldsOf = (t: SourceType): string[] => [...SOURCE_TYPES[t].required, ...SOURCE_TYPES[t].optional];

/**
 * Spec §2 suggestion: headers and labels are compared after `fold` (lowercase, Turkish folding, no spaces or punctuation).
 * A header is used for one field only; required fields choose first.
 */
export function suggestMapping(t: SourceType, headers: string[]): Record<string, string> {
  const folded = headers.map(fold);
  const used = new Set<number>();
  const out: Record<string, string> = {};
  for (const f of fieldsOf(t)) {
    const meta = FIELDS[f]!;
    for (const name of [meta.label, ...meta.aliases]) {
      const i = folded.findIndex((h, k) => !used.has(k) && h !== "" && h === fold(name));
      if (i >= 0) { used.add(i); out[f] = headers[i]!; break; }
    }
  }
  return out;
}

/**
 * Kayıtlı profil yokken kaynak tipi tahmini (#292): başlıklardan zorunlu alanlarının hepsi önerilen tek tip.
 * Birden çok tip tam eşleşiyorsa en çok alanı (zorunlu + isteğe bağlı) eşleyen; yine eşitse null (kullanıcıya sorulur).
 */
export function guessSourceType(headers: string[]): SourceType | null {
  const scored = (Object.keys(SOURCE_TYPES) as SourceType[])
    .filter((t) => SOURCE_TYPES[t].required.every((f) => suggestMapping(t, headers)[f]))
    .map((t) => [t, Object.keys(suggestMapping(t, headers)).length] as const)
    .sort((a, b) => b[1] - a[1]);
  return scored.length === 1 || (scored.length > 1 && scored[0]![1] > scored[1]![1]) ? scored[0]![0] : null;
}
